/**
 * MediaLightbox — Shared fullscreen media viewer
 *
 * DOM-driven: scans elements with [data-lightbox-src] within a scoped container
 * and builds a navigable image/video gallery.
 *
 * Interface:
 *   MediaLightbox.init(id, scopeSelector?)  — bind lightbox to a section
 *   MediaLightbox.open(id, index)           — open lightbox at index
 *   MediaLightbox.close(id)                 — close lightbox
 *
 * Required data attributes on trigger elements:
 *   data-lightbox-src    — media URL
 *   data-lightbox-type   — 'image' or 'video'
 *   data-lightbox-poster — (optional) poster for video
 *
 * @version 1.0.0
 */
(function () {
  'use strict';

  var instances = {};

  function buildMediaList(scopeEl) {
    var seen = {};
    var items = [];
    var els = scopeEl.querySelectorAll('[data-lightbox-src]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var src = el.getAttribute('data-lightbox-src') || '';
      var type = el.getAttribute('data-lightbox-type') || 'image';
      if (!src || seen[src]) continue;
      seen[src] = true;
      items.push({
        type: type,
        src: src,
        poster: el.getAttribute('data-lightbox-poster') || '',
        preTitle: el.getAttribute('data-lightbox-pre-title') || '',
        title: el.getAttribute('data-lightbox-title') || '',
        desc: el.getAttribute('data-lightbox-desc') || '',
        btnText: el.getAttribute('data-lightbox-btn-text') || '',
        btnUrl: el.getAttribute('data-lightbox-btn-url') || ''
      });
    }
    return items;
  }

  function renderItem(inst, idx, isNavigation) {
    var lbMedia = inst.lbMedia;
    if (!lbMedia) return;
    var item = inst.mediaList[idx];
    if (!item) return;

    // Lock container size during swap to prevent jitter
    if (isNavigation && lbMedia.firstElementChild) {
      var rect = lbMedia.getBoundingClientRect();
      lbMedia.style.width = rect.width + 'px';
      lbMedia.style.height = rect.height + 'px';
    }

    var oldEl = lbMedia.querySelector('.mlb-media-inner');
    var doSwap = function () {
      // Pause old video before removing
      var oldVid = lbMedia.querySelector('video');
      if (oldVid) oldVid.pause();
      lbMedia.innerHTML = '';

      var innerWrap = document.createElement('div');
      innerWrap.className = 'mlb-media-inner';

      // Create new element
      var newEl;
      if (item.type === 'video') {
        newEl = document.createElement('video');
        newEl.controls = true;
        newEl.autoplay = true;
        newEl.muted = true;
        newEl.defaultMuted = true;
        newEl.playsInline = true;
        newEl.setAttribute('autoplay', '');
        newEl.setAttribute('muted', '');
        newEl.setAttribute('playsinline', '');
        newEl.setAttribute('controlslist', 'nofullscreen nodownload noremoteplayback noplaybackrate');
        newEl.disablePictureInPicture = true;
        var srcEl = document.createElement('source');
        srcEl.src = item.src;
        srcEl.type = 'video/mp4';
        newEl.appendChild(srcEl);
      } else {
        newEl = document.createElement('img');
        newEl.src = item.src;
        newEl.alt = '';
      }

      innerWrap.appendChild(newEl);

      // Render content overlay (title / desc / button) if present
      var hasContent = item.preTitle || item.title || item.desc || item.btnText;
      if (hasContent) {
        var contentEl = document.createElement('div');
        contentEl.className = 'mlb-content';
        var html = '';
        if (item.preTitle) html += '<div class="mlb-content-pre-title">' + item.preTitle + '</div>';
        if (item.title) html += '<div class="mlb-content-title">' + item.title + '</div>';
        if (item.desc) html += '<div class="mlb-content-desc">' + item.desc + '</div>';
        if (item.btnText && item.btnUrl) {
          html += '<a class="mlb-content-btn" href="' + item.btnUrl + '">' + item.btnText + '</a>';
        }
        contentEl.innerHTML = html;
        innerWrap.appendChild(contentEl);
      }

      lbMedia.appendChild(innerWrap);

      // Reveal once ready
      var reveal = function () {
        innerWrap.classList.add('mlb-visible');
        lbMedia.style.width = '';
        lbMedia.style.height = '';
        if (item.type === 'video' && newEl.play) {
          var p = newEl.play();
          if (p && p.catch) p.catch(function(){}); // Ignore auto-play blocking errors
        }
      };

      if (item.type === 'video') {
        newEl.addEventListener('loadeddata', reveal, { once: true });
        newEl.addEventListener('error', reveal, { once: true });
      } else {
        if (newEl.complete) {
          reveal();
        } else {
          newEl.addEventListener('load', reveal, { once: true });
          newEl.addEventListener('error', reveal, { once: true });
        }
      }
    };

    // Fade-out then swap, or swap immediately
    if (isNavigation && oldEl) {
      oldEl.classList.remove('mlb-visible');
      setTimeout(doSwap, 200);
    } else {
      doSwap();
    }

    // Counter
    if (inst.lbCounter) {
      inst.lbCounter.textContent = inst.mediaList.length > 1
        ? (idx + 1) + ' / ' + inst.mediaList.length
        : '';
    }
    // Nav visibility
    if (inst.lbPrev) inst.lbPrev.classList.toggle('is-hidden', idx === 0);
    if (inst.lbNext) inst.lbNext.classList.toggle('is-hidden', idx === inst.mediaList.length - 1);
  }

  function openLightbox(inst, idx) {
    inst.mediaList = buildMediaList(inst.scopeEl);
    inst.currentIdx = Math.max(0, Math.min(idx, inst.mediaList.length - 1));
    renderItem(inst, inst.currentIdx, false);
    inst.lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox(inst) {
    var vid = inst.lbMedia && inst.lbMedia.querySelector('video');
    if (vid) vid.pause();
    inst.lbMedia.innerHTML = '';
    inst.lbMedia.style.width = '';
    inst.lbMedia.style.height = '';
    inst.lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function navigate(inst, dir) {
    var next = inst.currentIdx + dir;
    if (next < 0 || next >= inst.mediaList.length) return;
    inst.currentIdx = next;
    renderItem(inst, inst.currentIdx, true);
  }

  /**
   * Initialize a lightbox instance for a given section.
   *
   * @param {string} id - Section ID used to find the lightbox DOM elements
   * @param {string} [scopeSelector] - Optional CSS selector for the scoping container.
   *                                    Defaults to '#shopify-section-{id}'.
   */
  function init(id, scopeSelector) {
    var lightbox = document.getElementById('mlb-lightbox-' + id);
    if (!lightbox) return;

    var scopeEl;
    if (scopeSelector) {
      scopeEl = document.querySelector(scopeSelector);
    }
    if (!scopeEl) {
      scopeEl = document.getElementById('shopify-section-' + id);
    }
    if (!scopeEl) return;

    var inst = {
      id: id,
      lightbox: lightbox,
      scopeEl: scopeEl,
      lbMedia: lightbox.querySelector('[data-mlb-media]'),
      lbClose: lightbox.querySelector('[data-mlb-close]'),
      lbPrev: lightbox.querySelector('[data-mlb-prev]'),
      lbNext: lightbox.querySelector('[data-mlb-next]'),
      lbCounter: lightbox.querySelector('[data-mlb-counter]'),
      mediaList: [],
      currentIdx: 0
    };

    instances[id] = inst;

    // Close button
    if (inst.lbClose) {
      inst.lbClose.addEventListener('click', function () { closeLightbox(inst); });
    }
    // Prev / Next
    if (inst.lbPrev) {
      inst.lbPrev.addEventListener('click', function () { navigate(inst, -1); });
    }
    if (inst.lbNext) {
      inst.lbNext.addEventListener('click', function () { navigate(inst, 1); });
    }
    // Click backdrop to close
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox(inst);
    });
    // Keyboard
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox(inst);
      if (e.key === 'ArrowLeft') navigate(inst, -1);
      if (e.key === 'ArrowRight') navigate(inst, 1);
    });

    // Auto-bind click handlers on trigger elements
    // Video play buttons: [data-lightbox-play] within scope
    scopeEl.querySelectorAll('[data-lightbox-play]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var card = btn.closest('[data-lightbox-src]');
        if (!card) return;
        var list = buildMediaList(scopeEl);
        var src = card.getAttribute('data-lightbox-src');
        var idx = list.findIndex(function (it) { return it.src === src; });
        openLightbox(inst, idx >= 0 ? idx : 0);
      });
    });

    // Image cards: [data-lightbox-click] within scope
    scopeEl.querySelectorAll('[data-lightbox-click]').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('a, button')) return;
        var list = buildMediaList(scopeEl);
        var src = card.getAttribute('data-lightbox-src');
        var idx = list.findIndex(function (it) { return it.src === src; });
        openLightbox(inst, idx >= 0 ? idx : 0);
      });
    });
  }

  // Public API
  window.MediaLightbox = {
    init: init,
    open: function (id, idx) {
      var inst = instances[id];
      if (inst) openLightbox(inst, idx);
    },
    close: function (id) {
      var inst = instances[id];
      if (inst) closeLightbox(inst);
    }
  };
})();
